import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import Button from "@/js/common/ui/Button";
import { exportToXlsx } from "@/js/common/ExcelExport";
import { fldInfsProps } from "@/js/common/ui/GridTable";
import * as Atom from "@/js/common/Atom";
import { useRecoilValue } from "recoil";

interface Props {
    systemCode: string;
}

const StatsSpcAcntgYrIncmExpndInq = ({ systemCode }: Props) => {
    const userInfo = useRecoilValue(Atom.userInfo);

    // 검색 조건
    const [acctYear, setAcctYear] = React.useState<string>(Util.getNowAcntgYear());

    // 로그인 기준 조직
    const [upOgnzCd] = React.useState<string>(userInfo.upOgnzCd);
    const [ognzCd] = React.useState<string>(userInfo.ognzCd);

    const [dataArr, setDataArr] = React.useState<any[]>([]);
    const [systemName, setSystemName] = React.useState<string>();

    const [totalIncome, setTotalIncome] = React.useState<string>("0");
    const [totalExpense, setTotalExpense] = React.useState<string>("0");
    const [totalDiff, setTotalDiff] = React.useState<string>("0");

    React.useEffect(() => {
        if (Util.isEmpty(acctYear) || Util.isEmpty(ognzCd)) {
            setDataArr([]);
            setTotalIncome("0");
            setTotalExpense("0");
            setTotalDiff("0");
            return;
        }

        AppContext.showSpinner();

        Axios.dataAccess({
            url: "system/stats/select-stats-bgt-spc-month.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                acntgYear: acctYear,
                ognzCd: ognzCd,
                upOgnzCd: upOgnzCd,
                bgtSeCd: systemCode,
            },
            onSuccessFn: (res: any) => {
                setDataArr(res.result.list);

                setTotalIncome(String(res.result.listSum.dpstAmt));
                setTotalExpense(String(res.result.listSum.implAmt));
                setTotalDiff(String(res.result.listSum.diffAmt));

                AppContext.hideSpinner();
            },
        });
    }, [acctYear, ognzCd, upOgnzCd, systemCode]);

    // 특별회계기금 타이틀 설정
    React.useEffect(() => {
        if (systemCode === "C1") {
            setSystemName("복지기금");
        } else if (systemCode === "C2") {
            setSystemName("자립기금");
        } else if (systemCode === "C3") {
            setSystemName("발전기금");
        } else if (systemCode === "C4") {
            setSystemName("희생자부조금");
        } else if (systemCode === "C5") {
            setSystemName("퇴직급여충당금");
        } else if (systemCode === "E1") {
            setSystemName("지부운영비");
        } else if (systemCode === "F1") {
            setSystemName("지부장직무활동비");
        } else if (systemCode === "G1") {
            setSystemName("지부대회비");
        }
    }, [systemCode]);

    /* =========================
       엑셀 컬럼 (camelCase)
       ========================= */
    const fldInfs: fldInfsProps[] = [
        { type: "text",  fldNm: "month",   width: 80,  headAlign: "center", dataAlign: "center", label: "월" },
        { type: "comma", fldNm: "dpstAmt", width: 160, headAlign: "center", dataAlign: "right",  label: "총 수입" },
        { type: "comma", fldNm: "implAmt", width: 160, headAlign: "center", dataAlign: "right",  label: "총 지출" },
        { type: "comma", fldNm: "diffAmt", width: 160, headAlign: "center", dataAlign: "right",  label: "통장잔액" },
    ];

    function downloadExcel() {
        if (dataArr.length === 0) {
            AppContext.openAlert({
                title: "데이터 확인",
                msg: "엑셀로 다운로드할 데이터가 없습니다.",
                icon: "warning",
            });
            return;
        }

        const totalRow = {
            month: "총 합계",
            dpstAmt: Number(Util.uncomma(totalIncome || "0")),
            implAmt: Number(Util.uncomma(totalExpense || "0")),
            diffAmt: Number(Util.uncomma(totalDiff || "0")),
            excelRowStyle : {
                fillColor: "FFEF99",
                fontColor: "000000",
                bold: true,
            },
        };

        const excelRows = [
            ...dataArr,
            totalRow,
        ];

        exportToXlsx({
            fileName: `${acctYear}년 ${systemName} 월별 수입·지출 통계`,
            title: `${acctYear}년 ${systemName} 월별 수입·지출 통계`,
            subTitleLeft: `${acctYear}회계년도`,
            subTitleRight: "(금액단위:원)",
            columns: fldInfs,
            rows: excelRows,
        });
    }

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            {/* 타이틀 */}
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    회계연도 월별 수입/지출 통계
                </span>
                <div className="bdt3 bdc007bff pb10" />
            </div>

            {/* 검색 조건 */}
            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC gap14 mb10">
                    {/* 본부(표시용) */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">본부</span>
                        <SelectBox
                            value={upOgnzCd}
                            width={140}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getUpOrgCodeArray("01")}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={true}
                            onChangeFunc={() => {}}
                        />
                    </div>

                    {/* 지부(표시용) */}
                    <div className="dpFlx aiC">
                        <span className="w40 fm-c fs15 fc333333 ls1 taL">지부</span>
                        <SelectBox
                            value={ognzCd}
                            width={150}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#F6F6F6"
                            codeArray={Util.getDownOrgCodeArray(upOgnzCd)}
                            placeholder="전체"
                            paddingRight={8}
                            disabled={true}
                            onChangeFunc={() => {}}
                        />
                    </div>

                    <div className="dpFlx aiC">
                        <span className="w80 fm-c fs15 fc333333 ls1 taL">회계연도</span>
                        <SelectBox
                            value={acctYear}
                            width={120}
                            height={36}
                            fontSize={14}
                            fontFamily="fm-c"
                            color="#333"
                            letterSpacing={-1}
                            borderColor="#ddd"
                            borderOnColor="#222"
                            borderRadius={2}
                            background="#fff"
                            codeArray={Util.getAcntgYearArray()}
                            placeholder="선택"
                            paddingRight={8}
                            onChangeFunc={(data: string) => setAcctYear(data)}
                        />
                    </div>

                    <div className="dpFlx mlA aiC gap12">
                        <Button
                            width={150}
                            height={40}
                            fontFamily="fm-b"
                            fontSize={16}
                            letterSpacing={-1}
                            variant="styExcelBtn"
                            borderRadius={6}
                            buttonClass="dpFlx aiC jcC"
                            buttonName="Excel 다운로드"
                            onClickFunc={downloadExcel}
                        />
                    </div>
                </div>
            </div>

            {/* 테이블 */}
            <div
                className="w100p"
                style={{
                    maxHeight: 545,
                    overflowY: "auto",
                    border: "1px solid #DDDDDD",
                    borderRadius: 8,
                }}
            >
                <table className="w100p tlF" style={{ borderCollapse: "collapse" }}>
                    <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                        <tr>
                            {["월", "총 수입", "총 지출", "통장잔액"].map((label) => (
                                <th
                                    key={label}
                                    className="bd1 bdcDDDDDD taC fm-b fs14 fc333333"
                                    style={{ padding: "10px 12px", background: "#EFF8FB" }}
                                >
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {dataArr.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="bd1 bdcDDDDDD p16 taC fm-c fs14 fc666666">
                                    조회 결과가 없습니다.
                                </td>
                            </tr>
                        ) : (
                            dataArr.map((r: any, idx: number) => (
                                <tr key={idx}>
                                    <td className="bd1 bdcDDDDDD p10 taC fm-c fs14">
                                        {r.month}
                                    </td>
                                    <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fc007bff">
                                        {Util.comma(String(r.dpstAmt))}
                                    </td>
                                    <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fcdc3545">
                                        {Util.comma(String(r.implAmt))}
                                    </td>
                                    <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fc333333">
                                        {Util.comma(String(r.diffAmt))}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>

                    <tfoot>
                        <tr style={{ position: "sticky", bottom: 0, background: "#E1E1E1" }}>
                            <td className="bd1 bdcDDDDDD p10 taC fm-c fs14 fw700">총 합계</td>
                            <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fc007bff">
                                {Util.comma(totalIncome)}
                            </td>
                            <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fcdc3545">
                                {Util.comma(totalExpense)}
                            </td>
                            <td className="bd1 bdcDDDDDD p10 taR fm-c fs14 fw700 fc333333">
                                {Util.comma(totalDiff)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

export default StatsSpcAcntgYrIncmExpndInq;